import { useEffect, useMemo } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { sheetAtom } from 'atoms/atoms'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import capitalize from 'lodash-es/capitalize'
import isEmpty from 'lodash-es/isEmpty'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMatch, useNavigate } from 'react-router-dom'
import { logSentryErrorAndGetTraceId } from '@node-space/utils'
import { PathNames } from 'constants/General'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useAddBeneficiaryMutationChallengeRequest } from 'hooks/mutations/useBeneficiariesMutation'
import { usePostContactInstrumentMutation } from 'hooks/mutations/usePostContactInstrumentMutation'
import { usePutContactInstrumentMutation } from 'hooks/mutations/usePutContactInstrumentMutation'
import { useCryptoCurrenciesQuery } from 'hooks/queries/useCryptoCurrenciesQuery'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useToastContext } from 'hooks/useToastContext'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse, ChallengeRequest } from 'types/beneficiaries'
import { ContactInstrumentPayload, ContactInstrumentProps, InstrumentType } from 'types/contacts'
import { SheetName, SheetState } from 'types/types'
import {
  contactAtom,
  contactInstrumentAtom,
  editingContactInstrumentAtom,
} from '../atoms/contactAtoms'
import {
  ContactInstrumentFormData,
  ContactInstrumentFormKeys,
  contactInstrumentSchema,
} from '../schemas/contactInstrumentSchema'
import { getContactInstrumentDefaultValues } from '../utils/utils'

export const useContactInstrumentForm = () => {
  const { t } = useTranslation()
  const addToast = useToastContext()
  const match = useMatch(PathNames.CRYPTO_CONTACTS)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const contact = useAtomValue(contactAtom)
  const setSheet = useSetAtom(sheetAtom)
  const [contactInstrument, setContactInstrument] = useAtom(contactInstrumentAtom)
  const setIsEditingContactInstrument = useSetAtom(editingContactInstrumentAtom)
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { mutate: postContactInstrument, isPending: isPostingContactInstrument } =
    usePostContactInstrumentMutation()
  const { mutate: putContactInstrument, isPending: isUpdatingContactInstrument } =
    usePutContactInstrumentMutation()
  const { mutate: mutatePostChallengeRequest, isPending: isPostingChallengeRequest } =
    useAddBeneficiaryMutationChallengeRequest()
  const { data: cryptoCurrencies, isPending: isLoadingCryptoCurrencies } =
    useCryptoCurrenciesQuery()

  const cryptoCurrenciesOptions = useMemo(() => {
    if (!!cryptoCurrencies?.length) {
      return cryptoCurrencies?.flatMap(cryptoCurrency =>
        cryptoCurrency?.protocols?.map(protocol => ({
          label: `${cryptoCurrency?.code} (${capitalize(protocol?.networkCode)})`,
          value: `${cryptoCurrency?.code} (${protocol?.networkCode})`,
          currencyCode: cryptoCurrency?.code,
          networkCode: protocol?.networkCode,
        }))
      )
    }
    return []
  }, [cryptoCurrencies])

  const formSchema = contactInstrumentSchema(t)

  const isEditingContactInstrument = !isEmpty(contactInstrument)

  useEffect(() => {
    setIsEditingContactInstrument(isEditingContactInstrument)
  }, [isEditingContactInstrument])

  const form = useForm<ContactInstrumentFormData>({
    defaultValues: getContactInstrumentDefaultValues(isEditingContactInstrument, contactInstrument),
    resolver: yupResolver(formSchema),
  })

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting },
  } = form

  const formValues = useWatch({ control })

  const { setInputField, setSelectField, setBaseField } = useFormFields<ContactInstrumentFormKeys>(
    formSchema,
    form,
    formValues
  )

  const formFields: { [key in ContactInstrumentFormKeys] } = {
    description: setInputField({
      key: 'description',
      placeholder: t('formFields.placeholder.description'),
    }),
    currencyAndNetwork: setSelectField({
      key: 'currencyAndNetwork',
      options: cryptoCurrenciesOptions,
      placeholder: t('select'),
    }),
    walletAddress: setInputField({
      key: 'walletAddress',
    }),
    otp: setBaseField({
      key: 'otp',
    }),
  }

  const handleOnError = (error: BaseErrorResponse, message: string) => {
    if (error?.status === 428) {
      postChallengeRequest(error)
      return
    }
    const sentryTraceId = logSentryErrorAndGetTraceId(message, error)
    setRequestError({
      handledMessage: error?.message,
      errorCode: error?.status,
      sentryTraceId,
      show: true,
    })
  }

  const onSubmit: SubmitHandler<typeof formValues> = () => {
    resetRequestError()
    const cryptoCurrency = cryptoCurrenciesOptions?.find(
      cryptoCurrencyOption => cryptoCurrencyOption?.value === formValues?.currencyAndNetwork
    )
    const instrumentPayload: ContactInstrumentPayload = {
      type: InstrumentType.CRYPTO,
      address: formValues?.walletAddress,
      network: cryptoCurrency?.networkCode,
      currency: cryptoCurrency?.currencyCode,
      description: formValues?.description,
    }
    const instrumentRequestPayload: ContactInstrumentProps = {
      ...(isEditingContactInstrument && { instrumentId: contactInstrument?.id }),
      contactId: contact?.id,
      instrument: instrumentPayload,
    }
    if (!isEditingContactInstrument) {
      postContactInstrument(instrumentRequestPayload, {
        onError: (error: BaseErrorResponse) => {
          handleOnError(error, 'postContactInstrument')
        },
      })
    } else {
      putContactInstrument(instrumentRequestPayload, {
        onError: (error: BaseErrorResponse) => {
          handleOnError(error, 'putContactInstrument')
        },
      })
    }
  }

  const postChallengeRequest = (error: BaseErrorResponse) => {
    const data: ChallengeRequest = {
      challenge: error?.data?.challenge?.value,
      tokenResponse: formValues?.otp,
    }
    mutatePostChallengeRequest(data, {
      onSuccess: () => {
        addToast({
          title: t(
            isEditingContactInstrument
              ? 'contacts.updateContactInstrumentSuccess'
              : 'contacts.addContactInstrumentSuccess',
            {
              description: formValues?.description,
            }
          ),
          state: 'success',
        })
        if (match) {
          navigate(`${PathNames.CRYPTO_CONTACTS}/${contact?.id}`)
        } else {
          // contact instrument only gets set on the contact instrument page
          setContactInstrument(null)
          queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACT] })
        }
        setSheet({
          [SheetName.INSTRUMENT]: SheetState.CLOSED,
        })
      },
      onError: (error: BaseErrorResponse) => {
        const sentryTraceId = logSentryErrorAndGetTraceId(
          'postChallengeRequest - contact instrument',
          error
        )
        setRequestError({
          handledMessage: error?.message,
          errorCode: error?.status,
          sentryTraceId,
          show: true,
        })
      },
    })
  }

  return {
    formFields,
    isLoadingCryptoCurrencies,
    errors,
    isSubmitting,
    isPostingContactInstrument,
    isPostingChallengeRequest,
    isEditingContactInstrument,
    isUpdatingContactInstrument,
    requestError,
    handleSubmit,
    onSubmit,
    setValue,
    clearErrors,
  }
}
